"use client";

import { memo, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ActivityChartProps } from "./types";
import { ActivityChartLegend } from "./ActivityChartLegend";
import { AreaChartContent } from "./AreaChartContent";
import { ChartContainer } from "./ChartContainer";

const ActivityChart = memo(function ActivityChart({
  data,
  title,
  height = 300,
  normalThreshold = 2,
  showLegend = true,
  withCard = true,
  chartType = "area",
  hideYAxis = false,
  showFirstMinute = false,
  displayMode = "minutes",
}: ActivityChartProps) {
  const t = useTranslations();

  const chartData = useMemo(() => {
    // ВАЖНО: showFirstMinute влияет не только на ticks.
    // Recharts считает домен X по данным; если в data нет minute=1,
    // то tick=1 может не отрисоваться, т.к. окажется вне домена.
    // Поэтому добавляем синтетическую точку minute=1, чтобы гарантировать
    // видимость первой минуты на оси и корректный домен.
    const hasMinute1 = data.some((p) => p.minute === 1);
    const normalizedData =
      showFirstMinute && !hasMinute1
        ? ([
            {
              minute: 1,
              value: 0,
              isNormal: true,
              isBurst: false,
            },
            ...data,
          ] as const)
        : data;

    const sorted = [...normalizedData].sort((a, b) => a.minute - b.minute);

    return sorted.map((point) => {
      const normalPart = Math.min(point.value, normalThreshold);
      const exceededPart = point.value > normalThreshold ? point.value - normalThreshold : 0;
      return {
        ...point,
        normalPart,
        exceededPart,
      };
    });
  }, [data, normalThreshold, showFirstMinute]);

  const maxValue = useMemo(() => {
    if (data.length === 0) return normalThreshold + 1;
    const max = data.reduce((acc, d) => Math.max(acc, d.value), normalThreshold);
    return Math.ceil(max) + 1;
  }, [data, normalThreshold]);

  const yTicks = useMemo(() => Array.from({ length: maxValue + 1 }, (_, i) => i), [maxValue]);

  const xTicks = useMemo(() => {
    if (data.length === 0) return [];
    const minutes = data.map((d) => d.minute);
    // Если нужно показать 1 в начале и её нет в данных
    if (showFirstMinute && !minutes.includes(1)) {
      return [1, ...minutes];
    }
    return minutes;
  }, [data, showFirstMinute]);

  const chartContent = (
    <AreaChartContent
      chartData={chartData}
      maxValue={maxValue}
      yTicks={yTicks}
      xTicks={xTicks}
      normalThreshold={normalThreshold}
      hideYAxis={hideYAxis}
      displayMode={displayMode}
      height={height}
    />
  );

  if (!withCard) {
    return (
      <ChartContainer height={height} showEmpty={data.length === 0}>
        {chartContent}
      </ChartContainer>
    );
  }

  // С Card обёрткой (default)
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
            {t("label-no-data")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            {title}
          </CardTitle>
          {showLegend && <ActivityChartLegend />}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {chartContent}
      </CardContent>
    </Card>
  );
});

export default ActivityChart;

// ========================================
// RE-EXPORTS для использования
// ========================================

// Типы
export type {
  ActivityDataPoint,
  MinuteHighlight,
  BurstData,
  ActivityChartProps,
} from "./types";
